import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cellDisplay } from "@/lib/reference-data-cell";

/** Longest text the balloon will show. A 200 kB jsonb blob would otherwise fill
 *  the viewport; the row-edit dialog is the place to read the whole thing. */
const BALLOON_LIMIT = 2000;

/**
 * A table cell that truncates with an ellipsis and, when it actually overflows,
 * reveals the full value in a tooltip on hover.
 *
 * Only an overflowing value becomes a Radix trigger — a short cell renders the
 * bare span it always did, so a 50-row page pays for a handful of tooltip
 * instances instead of one per cell.
 *
 * The caller provides the `TooltipProvider` (one per table, not per cell). The
 * trigger stays a `<span>`, never a button: no focus steal and no tab-order
 * noise across dozens of rows, and the click keeps bubbling so a clickable row
 * still opens. `pointer-events-none` on the content keeps the balloon from
 * swallowing that click.
 */
export function TruncatedCell({ value, className }: { value: unknown; className?: string }) {
  const { text, truncated } = cellDisplay(value);
  const span = <span className={className ?? "block max-w-[320px] truncate"}>{text}</span>;

  if (!truncated) return span;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{span}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-[420px] whitespace-pre-wrap break-words font-mono text-[11px] leading-snug pointer-events-none"
      >
        {text.slice(0, BALLOON_LIMIT)}
      </TooltipContent>
    </Tooltip>
  );
}
